import React, { useEffect, useState } from 'react';
import { Checkbox, TextField, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { ICustomAttributes, IDeliveryType } from '../../../models/configuration/plugin/IConfiguration';
import { IPENVendor, IframeVendor, VerificationVendorType, FormFreeLenderID, attributeOrder, showIPENVendor, showVerificationVendorType, showFormFreeLenderID } from '../../../constants/CustomAttributeConstants';
import { DtLabel } from '../../ui/label';
import { useFeatureFlag, FeatureFlags } from '../../../featureFlags';
import { DELIVERY_PREFIX } from '../../../utils/constants';

const initialCustomAttributesState: ICustomAttributes = {
    IPENVendor: null,
    IframeVendor: null,
    VerificationVendorType: null,
    FormFreeLenderID: null,
    SolexDocEditorAttr: false,
};

interface CustomAttributesProps {
    customAttributes: ICustomAttributes | undefined;
    updateParent: (attributes: ICustomAttributes) => void;
    selectedDeliveryType: IDeliveryType | null;
}

const CustomAttributes: React.FC<CustomAttributesProps> = ({
    customAttributes,
    updateParent,
    selectedDeliveryType
}) => {
    const [customAttributesState, setCustomAttributesState] = useState<ICustomAttributes>(initialCustomAttributesState);

    // Feature flag for Solex Doc Editor checkbox visibility (LaunchDarkly)
    const isSolexEditorFlagEnabled = useFeatureFlag(FeatureFlags.UI_SOLEX_EDITOR_ENABLED);

    // Configuration flag for Solex Doc Editor (client/org level) - direct Redux access
    const isSolexEditorConfigEnabled = useSelector(
        (state: RootState) => state.configPlugInData.data?.Configuration?.EnableSolexDocEditor ?? false
    );

    // Helper function to check if delivery type is eligible for Solex Doc Editor
    // Eligible: any delivery type ID starting with eSign or eClose
    // Includes primary (eSign), secondary (eSignSecondary), and children (eClose-RON)
    // Normalize to lowercase to handle mixed casing (consistent with OrderButton.tsx pattern)
    const isSolexEligibleDeliveryType = (deliveryTypeId: string | undefined): boolean => {
        if (!deliveryTypeId) return false;
        return deliveryTypeId.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN) ||
               deliveryTypeId.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE);
    };

    // Set the initial state if customAttributes are present
    useEffect(() => {
        if (customAttributes) {
            setCustomAttributesState(customAttributes);
        }
    }, [customAttributes]);

    // Auto-clear Solex flag when delivery type becomes ineligible (EN-3255)
    // Prevents stale flag from remaining set for eDisclose after removing it from eligible types
    useEffect(() => {
        if (selectedDeliveryType && customAttributes) {
            const isEligible = isSolexEligibleDeliveryType(selectedDeliveryType.Id);
            if (!isEligible && customAttributes.SolexDocEditorAttr) {
                // Use prop data directly (not stale local state) to preserve other attributes
                // This prevents overwriting new delivery type's vendor fields with old values
                const updatedState = { ...customAttributes, SolexDocEditorAttr: false };
                updateParent(updatedState);
            }
        }
    }, [selectedDeliveryType, customAttributes, updateParent]);

    const handleInputChange = (key: keyof ICustomAttributes) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedState = { ...customAttributesState, [key]: event.target.value };
        setCustomAttributesState(updatedState);
        updateParent(updatedState);
    };

    const handleSolexDocEditorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedState = {
            ...customAttributesState,
            SolexDocEditorAttr: event.target.checked
        };
        setCustomAttributesState(updatedState);
        updateParent(updatedState);
    };

    function updateFriendly(key: string): React.ReactNode {
        switch (key) {
            case IframeVendor:
                return 'Iframe Vendor';
            case IPENVendor:
                return 'IPEN Vendor';
            case VerificationVendorType:
                return 'Verification of Information Vendor Type';
            case FormFreeLenderID:
                return 'Freeform Vendor ID';
        }
    }

    const shouldDisplayAttribute = (selectedDeliveryType: IDeliveryType | null, attribute: string) => {
        switch (attribute) {
            case IPENVendor:
                return showIPENVendor.includes(selectedDeliveryType?.Id || "");
            case  VerificationVendorType:
                return showVerificationVendorType.includes(selectedDeliveryType?.Id || "");
            case FormFreeLenderID:
                return showFormFreeLenderID.includes(selectedDeliveryType?.Id || "");
        }
        return true;
    }

    return (
       <>
    {attributeOrder.map((key) => {
        return shouldDisplayAttribute(selectedDeliveryType, key) ? (
            <Box
                key={key}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                }}
            >
                <DtLabel sx={{ flex: 3, mr: 1 }}>{updateFriendly(key)}</DtLabel>
                <TextField
                    sx={{ flex: 9 }}
                    value={customAttributesState[key as keyof ICustomAttributes] || ''}
                    onChange={handleInputChange(key as keyof ICustomAttributes)}
                    size="small"
                    variant="outlined"
                />
            </Box>
        ) : null;
    })}

    {/* Enable Solex Doc Editor checkbox - conditionally rendered */}
    {isSolexEditorFlagEnabled && isSolexEditorConfigEnabled && isSolexEligibleDeliveryType(selectedDeliveryType?.Id) && (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
            }}
        >
            <DtLabel id="solex-doc-editor-label" sx={{ flex: 3, mr: 1 }}>Enable Solex Doc Editor</DtLabel>
            <Box sx={{ flex: 9 }}>
                <Checkbox
                    checked={customAttributesState.SolexDocEditorAttr ?? false}
                    onChange={handleSolexDocEditorChange}
                    data-testid="solex-doc-editor-checkbox"
                    inputProps={{ 'aria-labelledby': 'solex-doc-editor-label' }}
                />
            </Box>
        </Box>
    )}
</>
    );
};

export default CustomAttributes;